<template>
	<v-dialog v-model="open" max-width="520" :attach="overlayTarget">
		<v-card class="points-edit-dialog">
			<v-card-title class="headline">
				<md-icon>edit</md-icon>
				{{ t('PointsEditTitle') }}
			</v-card-title>

			<v-card-text class="points-edit-dialog__body">
				<p class="points-edit-dialog__sub">{{ t('PointsEditSub') }}</p>

				<div v-if="entry" class="points-edit-dialog__promo">
					<div class="points-edit-dialog__promo-name">{{ entry.promoName }}</div>
					<div class="points-edit-dialog__promo-meta">
						<span>
							<md-icon>calendar_today</md-icon>
							{{ formatDate(entry.date) }}
						</span>
						<span>
							<md-icon>location_on</md-icon>
							{{ entry.location }}
						</span>
					</div>
				</div>

				<v-alert type="info" variant="tonal" density="compact">{{ t('PointsEditNotice') }}</v-alert>

				<div>
					<label class="points-edit-dialog__label">{{ t('PointsCount') }}</label>
					<div class="points-edit-dialog__stepper">
						<v-btn icon="remove" variant="outlined" density="comfortable" @click="decrement" />
						<v-text-field v-model.number="editablePoints" type="number" hide-details density="comfortable" variant="outlined" class="points-edit-dialog__points-input" />
						<v-btn icon="add" variant="outlined" density="comfortable" @click="increment" />
					</div>
					<div class="points-edit-dialog__hint">
						<span>{{ t('PointsInitialClaim') }} : {{ entry?.pointsClaimed ?? 0 }} {{ t('pts') }}</span>
						<v-chip v-if="delta !== 0" :color="delta > 0 ? 'success' : 'error'" size="small" variant="tonal">{{ delta > 0 ? '+' : '' }}{{ delta }} {{ t('pts') }}</v-chip>
					</div>
				</div>

				<div>
					<label class="points-edit-dialog__label">{{ t('Comment') }}</label>
					<v-textarea v-model="comment" rows="3" variant="outlined" density="comfortable" :counter="500" :maxlength="500" hide-details="auto" />
				</div>

				<p class="points-edit-dialog__footer-hint">{{ t('PointsEditValidatorHint') }}</p>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="open = false">{{ t('Cancel') }}</v-btn>
				<v-btn variant="tonal" :loading="loading" @click="submit">{{ t('Send') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useTranslator } from '@/utils/useTranslator';
import { updatePointsClaim } from '@/api/sales/points';

const t = useTranslator();

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	entry: { type: Object, default: null },
	overlayTarget: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'updated']);

const open = ref(props.modelValue);
const editablePoints = ref(0);
const comment = ref('');
const loading = ref(false);

const delta = computed(() => editablePoints.value - (props.entry?.pointsClaimed ?? 0));

watch(
	() => props.modelValue,
	(v) => {
		open.value = v;
		if (v && props.entry) {
			editablePoints.value = props.entry.pointsClaimed ?? 0;
			comment.value = props.entry.comment ?? '';
		}
	}
);

watch(open, (v) => emit('update:modelValue', v));

function increment() {
	editablePoints.value = (editablePoints.value || 0) + 10;
}
function decrement() {
	editablePoints.value = Math.max(0, (editablePoints.value || 0) - 10);
}

async function submit() {
	if (!props.entry) return;
	loading.value = true;
	try {
		await updatePointsClaim({
			claimId: props.entry.id,
			pointsClaimed: editablePoints.value,
			comment: comment.value,
		});
		emit('updated');
		open.value = false;
	} finally {
		loading.value = false;
	}
}

function formatDate(iso) {
	if (!iso) return '';
	const d = new Date(iso);
	return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
</script>
